import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    getEventDivisionFromPlayerDivision,
    hasAdminAddPaymentRequiredInvitation,
    hasPlayerInvitation,
    isDivisionCustomRegistrationDateClosed,
} from '../../../../../utils/events';
import { registrationStateTypeIds } from '../../../../../utils/eventConstants';
import { getInvitationObjectFromDivision, getInvitationObjectFromPreRegInvite, InvitationTypes } from './utils';
import { declinePartnerInvitations, declineAdminAddedDivisions, declinePreRegInvitation, setDeclineInvitationData } from '../../../../../actions/event-profile-actions';

import PopupV3 from '../../../../../components/Popup/PopupV3';
import Invitation from './Invitation';

import styles from './playerPendingInvitations.scss';
import { eventShape } from '../../../../../propTypes';

const PlayerPendingInvitations = (props) => {
    const {
        event,
        currentMemberRegistration,
        clubVerificationFee,
        gotoRegistration,
        hideInvitations,
    } = props;

    const dispatch = useDispatch();

    const { declineInvitationData } = useSelector(({ eventProfile }) => {
        const { declineInvitationData } = eventProfile;
        return { declineInvitationData };
    });

    const [invitations, setInvitations] = useState([]);
    const [showInvites, setShowInvites] = useState(false);
    const [inviteToDecline, setInviteToDecline] = useState(null);

    const registrationDivisionDict = useMemo(() => {
        return (currentMemberRegistration?.registeredDivisions || [])
            .reduce((acc, div) => {
                acc[div.id] = div;
                return acc;
            }, {});
    }, [currentMemberRegistration?.registeredDivisions]);

    useEffect(() => {
        if (!event && !currentMemberRegistration) {
            return;
        }

        const enrichDivisionWithPartners = (division) => {
            const registrationDiv = registrationDivisionDict[division.id];
            if (registrationDiv) {
                return { ...division, partner: registrationDiv.partner };
            }

            return division;
        };

        let updatedInvitations = [];
        const isRegistrationClosed = event?.registrationStateId === registrationStateTypeIds.REGISTRATION_CLOSED;
        const isAllowedToPreRegister = event?.hasEligiblePlayersList && event?.currentMember?.isEligibleForEvent;
        const userRegisteredDivisions = event?.currentMember?.registeredDivisions || [];

        if (!isRegistrationClosed) {
            const divisionsWithPendingInvitations = userRegisteredDivisions
                .filter(hasPlayerInvitation)
                .map(getEventDivisionFromPlayerDivision(event))
                .map(enrichDivisionWithPartners)
                .filter((div => !isDivisionCustomRegistrationDateClosed(div))) || [];

            updatedInvitations = [
                ...updatedInvitations,
                ...divisionsWithPendingInvitations
                    .map(division => getInvitationObjectFromDivision(
                        division,
                        InvitationTypes.PLAYER_INVITATION,
                    )),
            ];
        }

        const divisionsWithPendingAdminAdds = userRegisteredDivisions
            .filter(hasAdminAddPaymentRequiredInvitation)
            .map(getEventDivisionFromPlayerDivision(event))
            .map(enrichDivisionWithPartners) || [];
        updatedInvitations = [
            ...updatedInvitations,
            ...divisionsWithPendingAdminAdds
                .map(division => getInvitationObjectFromDivision(
                    division,
                    InvitationTypes.ADMIN_ADD,
                )),
        ];

        if (event?.eventStateId === registrationStateTypeIds.PRE_REGISTRATION && isAllowedToPreRegister) {
            const userPreRegInvitations = event?.currentMember?.memberPreRegistrationInvites || [];
            updatedInvitations = [
                ...updatedInvitations,
                ...userPreRegInvitations
                    .map(getInvitationObjectFromPreRegInvite),
            ];
        }

        const existingInvitationIds = invitations.map(invitation => invitation.id);
        const newInvitationIds = updatedInvitations.map(invitation => invitation.id);
        const hasChanges = newInvitationIds.some(id => !existingInvitationIds.includes(id)) ||
            existingInvitationIds.some(id => !newInvitationIds.includes(id));

        if (hasChanges) {
            setInvitations(updatedInvitations);
            setInviteToDecline(null);
        }
    }, [event, registrationDivisionDict]);

    useEffect(() => {
        setShowInvites(invitations?.length > 0);
    }, [invitations]);

    useEffect(() => {
        if (declineInvitationData) {
            const { divisionId, type } = declineInvitationData;
            const invitation = invitations.find(inv => inv.divisionId === divisionId && inv.type === type);
            if (invitation) {
                setInviteToDecline({
                    ...invitation,
                });
            }
        }
    }, [declineInvitationData]);

    const onInvitationsPopupClose = useCallback(() => {
        setShowInvites(false);
        setInviteToDecline(null);

        if (declineInvitationData) {
            dispatch(setDeclineInvitationData(null));
        }
    }, [declineInvitationData]);

    const onDeclineInvitationClick = (invitation) => {
        setInviteToDecline(invitation);
    };

    const onConfirmDeclineClick = () => {
        if (inviteToDecline?.type === InvitationTypes.PLAYER_INVITATION) {
            const division = inviteToDecline?.data || {};
            dispatch(declinePartnerInvitations(event.id, [division], true));
        } else if (inviteToDecline?.type === InvitationTypes.ADMIN_ADD) {
            const division = inviteToDecline?.data || {};
            dispatch(declineAdminAddedDivisions(event.id, [division], true));
        } else if (inviteToDecline?.type === InvitationTypes.PRE_REGISTRATION) {
            dispatch(declinePreRegInvitation(event.id, inviteToDecline?.data?.preRegistrationInviteId, true));
        } else {
            // Do nothing for now, as we don't have a decline action for this type
        }
        setInviteToDecline(null);
        if (declineInvitationData) {
            dispatch(setDeclineInvitationData(null));
        }
    };

    const onClickContinueToRegister = () => {
        setShowInvites(false);
        setInviteToDecline(null);
        if (declineInvitationData) {
            dispatch(setDeclineInvitationData(null));
        }
        gotoRegistration();
    };

    return (
        <div className={styles.playerPendingInvitations}>
            <PopupV3
                title="Are you sure you want to decline?"
                message={(
                    <div className={styles.content}>
                        <div className="divider mt8 mb8" />
                        <Invitation
                            invitation={inviteToDecline}
                            hideDeclineButton={true}
                            event={event}
                            clubVerificationFee={clubVerificationFee}
                        />
                    </div>
                )}
                noIcon={true}
                isOpen={!hideInvitations && inviteToDecline}
                confirmButtonLabel="Decline"
                onConfirmClick={onConfirmDeclineClick}
                showCancelButton={true}
                showConfirmButton={true}
                showCloseButton={true}
                cancelButtonLabel="Register"
                onCancelClick={onClickContinueToRegister}
                onCloseButtonClick={onInvitationsPopupClose}
                showSupportLink={false}
                className={styles.popUp}
            />
            <PopupV3
                title="You have been invited to play in the following"
                message={(
                    <div className={styles.content}>
                        {invitations.length > 1 && (
                            <p className={`mt8 mb8`} data-testid="modal.player-invitations.instructions">
                                Review the invitations below. You may decline any individual invite before proceeding. Click &#39;Continue to Register&#39; to register for all remaining invites shown.
                            </p>
                        )}
                        <div className="divider mt8 mb8" />
                        {invitations.map((invitation) => {
                            return (
                                <Invitation
                                    key={invitation.id}
                                    invitation={invitation}
                                    hideDeclineButton={false}
                                    onDeclineClick={onDeclineInvitationClick}
                                    event={event}
                                    clubVerificationFee={clubVerificationFee}
                                />
                            );
                        })}
                        <button
                            type="button"
                            className="btn btn-primary btn-lg btn-block text-uppercase mt16"
                            onClick={onClickContinueToRegister}
                            data-testid="modal.player-invitations.continue-to-register-btn"
                        >
                            Continue to Register
                        </button>
                        <div className="text-small text-center mt16" data-testid="modal.player-invitations.reminder-text">
                            {`Reminder: This modal will reappear until you have accepted or declined ${invitations.length === 1 ? 'the invite' : 'all registration invites'}.`}
                        </div>
                    </div>
                )}
                noIcon={true}
                isOpen={!hideInvitations && showInvites && !inviteToDecline}
                showCancelButton={false}
                showConfirmButton={false}
                showCloseButton={true}
                onCancelClick={onInvitationsPopupClose}
                showSupportLink={false}
                className={styles.popUp}
            />
        </div>
    );
};

PlayerPendingInvitations.propTypes = {
    event: eventShape,
    currentMemberRegistration: PropTypes.shape({
        registeredDivisions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        })),
        preRegInvitations: PropTypes.arrayOf(PropTypes.shape({
            email: PropTypes.string,
            status: PropTypes.string,
            divisionId: PropTypes.number,
        })),
    }),
    clubVerificationFee: PropTypes.number,
    gotoRegistration: PropTypes.func.isRequired,
    hideInvitations: PropTypes.bool,
};

PlayerPendingInvitations.defaultProps = {
    clubVerificationFee: 0,
    hideInvitations: false,
    event: null,
    currentMemberRegistration: null,
};

export default PlayerPendingInvitations;
